import type { FieldConversion } from "types/dashboard.types"

export interface TelemetryFieldDef {
  unit: string
  measurement?: string
  displayUnit?: string
  conversion?: FieldConversion
  decimalPlaces?: number
  /**
   * Opts the field into SI prefixing (`1500 A` -> `1.5 kA`). Only set it on units that can
   * actually carry a prefix: on `%`, `°C` or `dBm` the prefix is pasted onto a unit that has
   * no scaled form, so 0.5 °C renders as "500 m°C".
   */
  formatUnits?: boolean
}

// ─── Powerline ────────────────────────────────────────────────────────────────

export const AMPACITY_AAR_FIELD = "ampacity.aar"
export const AMPACITY_AAR_CRITICAL_SPAN_FIELD = "ampacity.aar.critical_span"
export const AMPACITY_DLR_FIELD = "ampacity.dlr"
export const AMPACITY_DLR_CRITICAL_SPAN_FIELD = "ampacity.dlr.critical_span"
export const AMPACITY_FORECAST_AAR_FIELD = "ampacity.forecast.aar"
export const AMPACITY_FORECAST_DLR_FIELD = "ampacity.forecast.dlr"
export const AMPACITY_SHORT_TERM_05_FIELD = "ampacity.short_term.05"
export const AMPACITY_SHORT_TERM_10_FIELD = "ampacity.short_term.10"
export const AMPACITY_SHORT_TERM_15_FIELD = "ampacity.short_term.15"
export const POWERLINE_CURRENT_FIELD = "current"

export const powerlineFields = {
  [AMPACITY_AAR_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_AAR_CRITICAL_SPAN_FIELD]: { unit: "", measurement: "dlr" },
  [AMPACITY_DLR_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_DLR_CRITICAL_SPAN_FIELD]: { unit: "", measurement: "dlr" },
  [AMPACITY_FORECAST_AAR_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_FORECAST_DLR_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_SHORT_TERM_05_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_SHORT_TERM_10_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [AMPACITY_SHORT_TERM_15_FIELD]: { unit: "A", measurement: "dlr", decimalPlaces: 2 },
  [POWERLINE_CURRENT_FIELD]: {
    unit: "A",
    measurement: "dlr",
    decimalPlaces: 2,
    formatUnits: true,
  },
} satisfies Record<string, TelemetryFieldDef>

// ─── Span ─────────────────────────────────────────────────────────────────────

export const SPAN_AMPACITY_AAR_FIELD = "ampacity.aar.span"
export const SPAN_AMPACITY_DLR_FIELD = "ampacity.dlr.span"
export const SPAN_AMPACITY_FORECAST_AAR_FIELD = "ampacity.forecast.aar.span"
export const SPAN_AMPACITY_FORECAST_DLR_FIELD = "ampacity.forecast.dlr.span"
// The DLR worker publishes the conductor temperature per span under a bare
// "temperature". `allEntityFields` is flat, so no other entity may claim that key.
export const SPAN_TEMPERATURE_FIELD = "temperature"

const spanAmpacityField = {
  unit: "A",
  measurement: "dlr",
  decimalPlaces: 2,
  formatUnits: true,
} satisfies TelemetryFieldDef

export const spanFields = {
  [SPAN_AMPACITY_AAR_FIELD]: spanAmpacityField,
  [SPAN_AMPACITY_DLR_FIELD]: spanAmpacityField,
  [SPAN_AMPACITY_FORECAST_AAR_FIELD]: spanAmpacityField,
  [SPAN_AMPACITY_FORECAST_DLR_FIELD]: spanAmpacityField,
  [SPAN_TEMPERATURE_FIELD]: { unit: "°C", measurement: "dlr", decimalPlaces: 2 },
} satisfies Record<string, TelemetryFieldDef>

// ─── Device ───────────────────────────────────────────────────────────────────

export const CUMULATIVE_FATIGUE_FIELD =
  "derived.bending_amplitudes.fatigue.cumulative_damage_percentage"
export const BENDING_MAX_DISPLACEMENT_FIELD =
  "derived.bending_amplitudes.max_displacement"
export const DEVICE_CURRENT_FIELD = "derived.current.value"
export const ICE_ACCRETION_SEVERITY_FIELD = "derived.icing.ice_accretion_severity"
export const ICE_ACCRETION_TYPE_FIELD = "derived.icing.ice_accretion_type"
export const MOTION_ALPHA_FIELD = "derived.motion.alpha"
export const MOTION_BETA_FIELD = "derived.motion.beta"
export const MOTION_JERK_NORM_FIELD = "derived.motion.history.jerk.norm"
export const SAG_HORIZONTAL_TENSION_FIELD = "derived.sag.horizontal_tension"
export const SAG_LOWEST_POINT_FIELD = "derived.sag.lowest_point.value"
export const SAG_MIDPOINT_FIELD = "derived.sag.midpoint.value"
export const SAG_TENSION_LOWER_SUPPORT_FIELD = "derived.sag.tension_at_the_lower_support"
export const SAG_TENSION_UPPER_SUPPORT_FIELD = "derived.sag.tension_at_the_upper_support"
export const SAG_VALUE_FIELD = "derived.sag.value"
export const SAG_THERMAL_HORIZONTAL_TENSION_FIELD =
  "derived.sag.thermal.horizontal_tension"
export const SAG_THERMAL_LOWEST_POINT_FIELD = "derived.sag.thermal.lowest_point.value"
export const SAG_THERMAL_MIDPOINT_FIELD = "derived.sag.thermal.midpoint.value"
export const SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD =
  "derived.sag.thermal.tension_at_the_lower_support"
export const SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD =
  "derived.sag.thermal.tension_at_the_upper_support"
export const SAG_THERMAL_VALUE_FIELD = "derived.sag.thermal.value"
export const AGGREGATED_INCLINATION_FIELD = "derived.tower_health.aggregated_inclination"
export const LAT_FIELD = "lat"
export const LON_FIELD = "lon"
export const NET_NAME_FIELD = "net.name"
export const NET_SIGNAL_FIELD = "net.signal"
export const NET_TRANSPORT_FIELD = "net.transport"
export const BATTERY_LEVEL_FIELD = "power.battery.level"
export const BATTERY_STATUS_FIELD = "power.battery.status"
export const TRANSVERSE_WIND_SPEED_FIELD =
  "sensors.motion.aeolian.transverse_wind_speed_estimate"
export const MOTION_ALERT_FIELD = "sensors.motion.alerts.motion"
export const CABLE_TEMPERATURE_FIELD = "sensors.thermal.temperatures.frame.cable_estimate"
export const WSTATION_HUMIDITY_FIELD = "sensors.weather.general.humidity"
export const WSTATION_PRESSURE_FIELD = "sensors.weather.general.pressure"
export const WSTATION_TEMPERATURE_FIELD = "sensors.weather.general.temperature"
export const WSTATION_SOLAR_IRRADIANCE_FIELD = "sensors.weather.light.solar_rad"
export const WSTATION_PRECIPITATION_FIELD = "sensors.weather.precipitation.intensity"
export const WSTATION_WIND_DIRECTION_FIELD = "sensors.weather.wind.direction"
export const WSTATION_WIND_SPEED_FIELD = "sensors.weather.wind.speed"
export const TILT_FRONT_FIELD = "tower.point.tilt.front"
export const TILT_FRONT_SMOOTHED_FIELD = "tower.point.tilt.front.agg"
export const TILT_HEADING_FIELD = "tower.point.tilt.heading"
export const TILT_HEADING_SMOOTHED_FIELD = "tower.point.tilt.heading.agg"
export const TILT_MAGNITUDE_FIELD = "tower.point.tilt.magnitude"
export const TILT_MAGNITUDE_SMOOTHED_FIELD = "tower.point.tilt.magnitude.agg"
export const TILT_RIGHT_FIELD = "tower.point.tilt.right"
export const TILT_RIGHT_SMOOTHED_FIELD = "tower.point.tilt.right.agg"

export const deviceFields = {
  [CUMULATIVE_FATIGUE_FIELD]: { unit: "%", decimalPlaces: 2 },
  [BENDING_MAX_DISPLACEMENT_FIELD]: { unit: "m", decimalPlaces: 2, formatUnits: true },
  [DEVICE_CURRENT_FIELD]: { unit: "A", decimalPlaces: 2, formatUnits: true },
  [ICE_ACCRETION_SEVERITY_FIELD]: { unit: "" },
  [ICE_ACCRETION_TYPE_FIELD]: { unit: "" },
  [MOTION_ALPHA_FIELD]: { unit: "°", decimalPlaces: 3 },
  [MOTION_BETA_FIELD]: { unit: "°", decimalPlaces: 3 },
  [MOTION_JERK_NORM_FIELD]: { unit: "m/s³", decimalPlaces: 4 },
  [SAG_HORIZONTAL_TENSION_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_LOWEST_POINT_FIELD]: { unit: "m", decimalPlaces: 2 },
  [SAG_MIDPOINT_FIELD]: { unit: "m", decimalPlaces: 2 },
  [SAG_TENSION_LOWER_SUPPORT_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_TENSION_UPPER_SUPPORT_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_VALUE_FIELD]: { unit: "m", decimalPlaces: 2 },
  [SAG_THERMAL_HORIZONTAL_TENSION_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_THERMAL_LOWEST_POINT_FIELD]: { unit: "m", decimalPlaces: 2 },
  [SAG_THERMAL_MIDPOINT_FIELD]: { unit: "m", decimalPlaces: 2 },
  [SAG_THERMAL_TENSION_LOWER_SUPPORT_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_THERMAL_TENSION_UPPER_SUPPORT_FIELD]: { unit: "N", decimalPlaces: 2 },
  [SAG_THERMAL_VALUE_FIELD]: { unit: "m", decimalPlaces: 2 },
  [AGGREGATED_INCLINATION_FIELD]: { unit: "°", decimalPlaces: 3 },
  [LAT_FIELD]: { unit: "" },
  [LON_FIELD]: { unit: "" },
  [NET_NAME_FIELD]: { unit: "" },
  [NET_SIGNAL_FIELD]: { unit: "dBm" },
  [NET_TRANSPORT_FIELD]: { unit: "" },
  [BATTERY_LEVEL_FIELD]: { unit: "%", decimalPlaces: 2 },
  [BATTERY_STATUS_FIELD]: { unit: "" },
  [TRANSVERSE_WIND_SPEED_FIELD]: { unit: "m/s", decimalPlaces: 2 },
  [MOTION_ALERT_FIELD]: { unit: "" },
  [CABLE_TEMPERATURE_FIELD]: { unit: "°C", decimalPlaces: 2 },
  [WSTATION_HUMIDITY_FIELD]: { unit: "%", decimalPlaces: 2 },
  [WSTATION_PRESSURE_FIELD]: { unit: "Pa", decimalPlaces: 2, formatUnits: true },
  [WSTATION_TEMPERATURE_FIELD]: { unit: "°C", decimalPlaces: 2 },
  [WSTATION_SOLAR_IRRADIANCE_FIELD]: { unit: "W/m²", decimalPlaces: 2 },
  [WSTATION_PRECIPITATION_FIELD]: { unit: "l/m²", decimalPlaces: 2 },
  [WSTATION_WIND_DIRECTION_FIELD]: {
    unit: "rad",
    displayUnit: "°",
    conversion: "radiansToDegrees",
    decimalPlaces: 2,
  },
  [WSTATION_WIND_SPEED_FIELD]: { unit: "m/s", decimalPlaces: 2 },
  [TILT_FRONT_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_FRONT_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_HEADING_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_HEADING_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_MAGNITUDE_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_MAGNITUDE_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_RIGHT_FIELD]: { unit: "°", decimalPlaces: 3 },
  [TILT_RIGHT_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
} satisfies Record<string, TelemetryFieldDef>

// ─── Tower ────────────────────────────────────────────────────────────────────

export const TOWER_BENDING_FIELD = "derived.tower_health.bending"
export const TOWER_TORSION_FIELD = "derived.tower_health.torsion"
export const TOWER_GLOBAL_LEAN_FRONT_FIELD = "tower.global.lean.front"
export const TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD = "tower.global.lean.front.agg"
export const TOWER_GLOBAL_LEAN_HEADING_FIELD = "tower.global.lean.heading"
export const TOWER_GLOBAL_LEAN_HEADING_SMOOTHED_FIELD = "tower.global.lean.heading.agg"
export const TOWER_GLOBAL_LEAN_MAGNITUDE_FIELD = "tower.global.lean.magnitude"
export const TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD =
  "tower.global.lean.magnitude.agg"
export const TOWER_GLOBAL_LEAN_RIGHT_FIELD = "tower.global.lean.right"
export const TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD = "tower.global.lean.right.agg"

export const towerFields = {
  [AGGREGATED_INCLINATION_FIELD]: {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  [TOWER_BENDING_FIELD]: { unit: "°", measurement: "tower_health", decimalPlaces: 3 },
  [TOWER_TORSION_FIELD]: { unit: "°", measurement: "tower_health", decimalPlaces: 3 },
  [TOWER_GLOBAL_LEAN_FRONT_SMOOTHED_FIELD]: {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  [TOWER_GLOBAL_LEAN_HEADING_SMOOTHED_FIELD]: {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  [TOWER_GLOBAL_LEAN_MAGNITUDE_SMOOTHED_FIELD]: {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  [TOWER_GLOBAL_LEAN_RIGHT_SMOOTHED_FIELD]: {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
} satisfies Record<string, TelemetryFieldDef>

// ─── Tower segments ───────────────────────────────────────────────────────────

/**
 * Per-segment tower-health metrics. The segment names come from the tower's own settings
 * (see `getTowerSegments`), so these names are built at runtime by `segmentField`
 * rather than being constants. The `<segment>` keys below are the pattern the resolver matches,
 * which is why this map is deliberately kept out of `allEntityFields` — a placeholder key never
 * matches a concrete field name.
 */
export const TOWER_SEGMENT_METRICS = [
  "bending.magnitude",
  "bending.longitudinal",
  "bending.lateral",
  "torsion",
] as const

export type TowerSegmentMetric = (typeof TOWER_SEGMENT_METRICS)[number]
export type TowerSegmentFieldName = `tower.${string}.${TowerSegmentMetric}.agg`

export const towerSegmentFields = {
  "tower.<segment>.bending.magnitude.agg": {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  "tower.<segment>.bending.longitudinal.agg": {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  "tower.<segment>.bending.lateral.agg": {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
  "tower.<segment>.torsion.agg": {
    unit: "°",
    measurement: "tower_health",
    decimalPlaces: 3,
  },
} satisfies Record<string, TelemetryFieldDef>

// ─── Weather service ──────────────────────────────────────────────────────────

export const WSERVICE_IRRADIANCE_FIELD = "direct_normal_irradiance"
export const WSERVICE_PRECIPITATION_FIELD = "precipitation"
export const WSERVICE_HUMIDITY_FIELD = "relative_humidity_2m"
export const WSERVICE_TEMPERATURE_FIELD = "temperature_2m"
export const WSERVICE_WIND_DIRECTION_FIELD = "wind_direction_10m"
export const WSERVICE_WIND_GUSTS_FIELD = "wind_gusts_10m"
export const WSERVICE_WIND_SPEED_FIELD = "wind_speed_10m"

export const weatherServiceFields = {
  [WSERVICE_IRRADIANCE_FIELD]: {
    unit: "W/m²",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_PRECIPITATION_FIELD]: {
    unit: "mm",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_HUMIDITY_FIELD]: {
    unit: "%",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_TEMPERATURE_FIELD]: {
    unit: "°C",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_WIND_DIRECTION_FIELD]: {
    unit: "°",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_WIND_GUSTS_FIELD]: {
    unit: "m/s",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
  [WSERVICE_WIND_SPEED_FIELD]: {
    unit: "m/s",
    measurement: "current_weather",
    decimalPlaces: 2,
  },
} satisfies Record<string, TelemetryFieldDef>

// ─── Front-computed keys ──────────────────────────────────────────────────────

export const FRONT_TILT_DRIFT_INSTANT_FIELD = "front_tilt.drift.instant"
export const FRONT_TILT_DRIFT_SMOOTHED_FIELD = "front_tilt.drift.smoothed"
export const RIGHT_TILT_DRIFT_INSTANT_FIELD = "right_tilt.drift.instant"
export const RIGHT_TILT_DRIFT_SMOOTHED_FIELD = "right_tilt.drift.smoothed"
export const MAGNITUDE_TILT_DRIFT_INSTANT_FIELD = "magnitude_tilt.drift.instant"
export const MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD = "magnitude_tilt.drift.smoothed"

/**
 * Tilt drift metrics computed in the front. Nothing queries them, so they are kept out of
 * `allEntityFields` and never reach the API documentation tables; they only need a name because
 * a table-stat row is matched to its value by `_field`, so the component has to invent one.
 *
 * The alpha/beta drift and amplitude are computed in the front too, but they are single stats:
 * `getAnglesDrift` returns a bare `{ _value }` with no row to match, so they need no key here.
 */
export const frontComputedFields = {
  [FRONT_TILT_DRIFT_INSTANT_FIELD]: { unit: "°", decimalPlaces: 3 },
  [FRONT_TILT_DRIFT_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
  [RIGHT_TILT_DRIFT_INSTANT_FIELD]: { unit: "°", decimalPlaces: 3 },
  [RIGHT_TILT_DRIFT_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
  [MAGNITUDE_TILT_DRIFT_INSTANT_FIELD]: { unit: "°", decimalPlaces: 3 },
  [MAGNITUDE_TILT_DRIFT_SMOOTHED_FIELD]: { unit: "°", decimalPlaces: 3 },
} satisfies Record<string, TelemetryFieldDef>

// ─── Lookups ──────────────────────────────────────────────────────────────────

/** Every queryable telemetry, flattened for the by-name resolver in `helpers/utils/telemetryFields`. */
export const allEntityFields = {
  ...powerlineFields,
  ...spanFields,
  ...deviceFields,
  ...towerFields,
  ...weatherServiceFields,
}
